import { type HTMLMotionProps, motion, useReducedMotion } from 'motion/react';

/**
 * `motion.<tag>` components are memoised by motion, so indexing this map
 * per render is free. Calling `motion.create()` per render is not: it mints a new
 * component type and remounts the subtree.
 */
const MOTION_TAGS = {
  article: motion.article,
  aside: motion.aside,
  div: motion.div,
  footer: motion.footer,
  header: motion.header,
  li: motion.li,
  main: motion.main,
  nav: motion.nav,
  section: motion.section,
  span: motion.span,
  ul: motion.ul,
} as const;

type MotionBoxTag = keyof typeof MOTION_TAGS;

export type MotionBoxProps = HTMLMotionProps<'div'> & {
  /** Element to render. Defaults to a `div`. */
  component?: MotionBoxTag;
};

/**
 * Animated layout element. Layout and appearance come from `className`; motion
 * props (`animate`, `layout`, `variants`, …) pass straight through.
 */
export function MotionBox({ component = 'div', initial, transition, ...props }: MotionBoxProps) {
  const prefersReducedMotion = useReducedMotion();
  // Every tag in the map shares the prop surface of `div` for what callers pass
  // (motion directives, className, style, aria). Without the cast TypeScript
  // demands an intersection of all the union members' props.
  const Tag = MOTION_TAGS[component] as typeof motion.div;

  // `transition` is dropped rather than zeroed: `duration: 0` still schedules a
  // frame and still defers an `AnimatePresence` unmount by one.
  if (prefersReducedMotion) return <Tag {...props} initial={false} />;

  return <Tag {...props} initial={initial} transition={transition} />;
}
